// src/components/digital_twin/TwinLoadingOverlay.js
// FlowSense MOW+ — staged loading for the Digital Twin: what is loading, how far it got, how long it
// took. Full screen until the network data is in; then a compact pill while the 3D scene builds,
// so the map is usable immediately instead of hidden behind a spinner.
import React, { memo } from 'react';
import { Box, Flex, HStack, VStack, Text, Icon, Progress, Spinner, Heading, useColorModeValue } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaCheckCircle, FaExclamationCircle, FaRegCircle, FaServer } from 'react-icons/fa';

const MotionFlex = motion(Flex);
const MotionBox = motion(Box);

const StageRow = ({ stage, muted }) => {
  const icon = stage.status === 'done' ? FaCheckCircle : stage.status === 'error' ? FaExclamationCircle : FaRegCircle;
  const color = stage.status === 'done' ? 'green.400' : stage.status === 'error' ? 'red.400' : muted;
  return (
    <HStack spacing={3} w="100%">
      {stage.status === 'active' ? <Spinner size="xs" color="purple.400" /> : <Icon as={icon} color={color} boxSize={3.5} />}
      <Text fontSize="sm" fontWeight={stage.status === 'active' ? 800 : 600} flex={1}>{stage.label}</Text>
      {stage.detail && <Text fontSize="xs" color={muted} fontFamily="mono">{stage.detail}</Text>}
    </HStack>
  );
};

function TwinLoadingOverlay({ stages = [], sceneProgress = null, fullScreen = true }) {
  const bg = useColorModeValue('rgba(247,250,252,0.96)', 'rgba(13,17,28,0.96)');
  const card = useColorModeValue('white', 'gray.800');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const sp = sceneProgress || {};
  const pct = sp.total ? Math.round(((sp.loaded + sp.failed) / sp.total) * 100) : null;
  const sceneBusy = sp.total > 0 && !sp.done;

  return (
    <AnimatePresence>
      {fullScreen ? (
        <MotionFlex key="full" initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
          position="absolute" inset="0" bg={bg} zIndex={20} align="center" justify="center" direction="column" p={6}>
          <VStack bg={card} p={6} borderRadius="2xl" boxShadow="2xl" spacing={4} w="100%" maxW="420px" borderWidth="1px" borderColor="purple.200">
            <Box h="3px" w="100%" bgGradient="linear(to-r, purple.500, blue.500, cyan.400)" borderRadius="full" />
            <Icon as={FaServer} boxSize={9} color="purple.400" />
            <Heading size="md" bgGradient="linear(to-r, purple.400, blue.400)" bgClip="text" fontWeight="900" textAlign="center">
              Initializing FlowSense Digital Twin
            </Heading>
            <VStack align="stretch" w="100%" spacing={2.5}>
              {stages.map((s) => <StageRow key={s.key} stage={s} muted={muted} />)}
            </VStack>
            <Progress w="100%" size="xs" colorScheme="purple" isIndeterminate={pct == null} value={pct ?? undefined} borderRadius="full" />
          </VStack>
        </MotionFlex>
      ) : sceneBusy ? (
        <MotionBox key="pill" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          position="absolute" top="72px" left="50%" transform="translateX(-50%)" zIndex={12}
          bg={card} px={4} py={2} borderRadius="full" boxShadow="lg" borderWidth="1px" borderColor="purple.200" minW="260px">
          <HStack spacing={3}>
            <Spinner size="xs" color="purple.400" />
            <Text fontSize="xs" fontWeight="800">Building 3D scene</Text>
            <Text fontSize="xs" fontFamily="mono" color={muted}>{(sp.loaded || 0).toLocaleString()} / {(sp.total || 0).toLocaleString()}</Text>
          </HStack>
          <Progress mt={1.5} size="xs" colorScheme="purple" value={pct ?? 0} borderRadius="full" />
        </MotionBox>
      ) : null}
    </AnimatePresence>
  );
}

export default memo(TwinLoadingOverlay);
